import { useRef, useState } from 'react'
import { errorMessage } from '../../api/client.ts'
import {
	type PendingGuest,
	useBringPending,
	useLeavePending,
	useMe,
	usePendingGuest,
} from '../../api/queries/auth.ts'
import { defineMessages, plural, useT } from '../../i18n/index.ts'
import { Button } from '../../ui/Button.tsx'
import { Dialog } from '../../ui/Dialog.tsx'
import { toast } from '../../ui/Toast.tsx'

const messages = defineMessages({
	en: {
		title: 'Work was left in this browser',
		body: 'Someone made this here before you signed in. If it is yours, bring it into your account. If not, leave it: it stays here for whoever made it.',
		more: (n: number) => `and ${n} more`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# student has handed in on these links.',
				other: '# students have handed in on these links.',
			}),
		yes: 'Yes, these are mine: bring them',
		no: 'No, leave them',
		brought: (n: number) =>
			plural(n, {
				one: 'Your # activity from before signing in is here.',
				other: 'Your # activities from before signing in are here.',
			}),
		broughtOther: 'What you made before signing in is here.',
	},
	ro: {
		title: 'Au rămas lucrări în acest browser',
		body: 'Cineva a creat aceste lucrări aici înainte să te autentifici. Dacă sunt ale tale, adaugă-le în contul tău. Dacă nu, lasă-le: rămân aici pentru cine le-a creat.',
		more: (n: number) => `și încă ${n}`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# elev a predat pe aceste linkuri.',
				few: '# elevi au predat pe aceste linkuri.',
				other: '# de elevi au predat pe aceste linkuri.',
			}),
		yes: 'Da, sunt ale mele: adaugă-le',
		no: 'Nu, lasă-le',
		brought: (n: number) =>
			plural(n, {
				one: 'Activitatea ta de dinainte de autentificare este aici.',
				few: 'Cele # activități ale tale de dinainte de autentificare sunt aici.',
				other: 'Cele # de activități ale tale de dinainte de autentificare sunt aici.',
			}),
		broughtOther: 'Ce ai creat înainte de autentificare este aici.',
	},
	ru: {
		title: 'В этом браузере остались работы',
		body: 'Кто-то создал это здесь до вашего входа. Если это ваше, перенесите в свой аккаунт. Если нет, оставьте: это останется здесь для того, кто это создал.',
		more: (n: number) => `и ещё ${n}`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# ученик сдал работу по этим ссылкам.',
				few: '# ученика сдали работу по этим ссылкам.',
				many: '# учеников сдали работу по этим ссылкам.',
				other: '# ученика сдали работу по этим ссылкам.',
			}),
		yes: 'Да, это моё: перенести',
		no: 'Нет, оставить',
		brought: (n: number) =>
			plural(n, {
				one: 'Ваше задание, созданное до входа, теперь здесь.',
				few: 'Ваши # задания, созданные до входа, теперь здесь.',
				many: 'Ваши # заданий, созданных до входа, теперь здесь.',
				other: 'Ваши # задания, созданные до входа, теперь здесь.',
			}),
		broughtOther: 'То, что вы создали до входа, теперь здесь.',
	},
	fr: {
		title: 'Du travail a été laissé dans ce navigateur',
		body: 'Quelqu’un a créé cela ici avant votre connexion. Si c’est à vous, ajoutez-le à votre compte. Sinon, laissez-le : il reste ici pour son auteur.',
		more: (n: number) => `et ${n} de plus`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# élève a rendu son travail via ces liens.',
				other: '# élèves ont rendu leur travail via ces liens.',
			}),
		yes: 'Oui, c’est à moi : l’ajouter',
		no: 'Non, le laisser',
		brought: (n: number) =>
			plural(n, {
				one: 'Votre # activité d’avant la connexion est ici.',
				other: 'Vos # activités d’avant la connexion sont ici.',
			}),
		broughtOther: 'Ce que vous avez créé avant la connexion est ici.',
	},
	it: {
		title: 'È rimasto del lavoro in questo browser',
		body: 'Qualcuno lo ha creato qui prima del tuo accesso. Se è tuo, portalo nel tuo account. Se no, lascialo: resta qui per chi lo ha creato.',
		more: (n: number) => `e altri ${n}`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# studente ha consegnato da questi link.',
				other: '# studenti hanno consegnato da questi link.',
			}),
		yes: 'Sì, è mio: portalo',
		no: 'No, lascialo',
		brought: (n: number) =>
			plural(n, {
				one: 'La tua # attività di prima dell’accesso è qui.',
				other: 'Le tue # attività di prima dell’accesso sono qui.',
			}),
		broughtOther: 'Ciò che hai creato prima dell’accesso è qui.',
	},
	kk: {
		title: 'Бұл браузерде жұмыстар қалды',
		body: 'Мұны сіз кірместен бұрын біреу осында жасаған. Егер бұл сіздікі болса, аккаунтыңызға көшіріңіз. Болмаса, қалдырыңыз: ол оны жасаған адамға осында қалады.',
		more: (n: number) => `тағы ${n}`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# оқушы осы сілтемелер арқылы жұмысын тапсырды.',
				other: '# оқушы осы сілтемелер арқылы жұмысын тапсырды.',
			}),
		yes: 'Иә, бұл менікі: көшіру',
		no: 'Жоқ, қалдыру',
		brought: (n: number) =>
			plural(n, {
				one: 'Кіруден бұрын жасаған # тапсырмаңыз осында.',
				other: 'Кіруден бұрын жасаған # тапсырмаңыз осында.',
			}),
		broughtOther: 'Кіруден бұрын жасағаныңыз осында.',
	},
	es: {
		title: 'Hay trabajo en este navegador',
		body: 'Alguien lo hizo aquí antes de que iniciaras sesión. Si es tuyo, pásalo a tu cuenta. Si no, déjalo: se queda aquí para quien lo hizo.',
		more: (n: number) => `y ${n} más`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# estudiante ha entregado en estos enlaces.',
				other: '# estudiantes han entregado en estos enlaces.',
			}),
		yes: 'Sí, es mío: pasarlo',
		no: 'No, dejarlo',
		brought: (n: number) =>
			plural(n, {
				one: 'Tu # actividad de antes de iniciar sesión está aquí.',
				other: 'Tus # actividades de antes de iniciar sesión están aquí.',
			}),
		broughtOther: 'Lo que creaste antes de iniciar sesión está aquí.',
	},
	pt: {
		title: 'Há trabalho neste navegador',
		body: 'Alguém fez isto aqui antes de você entrar. Se for seu, traga para a sua conta. Se não, deixe: fica aqui para quem fez.',
		more: (n: number) => `e mais ${n}`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# aluno entregou por estes links.',
				other: '# alunos entregaram por estes links.',
			}),
		yes: 'Sim, é meu: trazer',
		no: 'Não, deixar',
		brought: (n: number) =>
			plural(n, {
				one: 'Sua # atividade de antes de entrar está aqui.',
				other: 'Suas # atividades de antes de entrar estão aqui.',
			}),
		broughtOther: 'O que você criou antes de entrar está aqui.',
	},
	de: {
		title: 'In diesem Browser liegt Arbeit',
		body: 'Jemand hat das hier erstellt, bevor du dich angemeldet hast. Wenn es deins ist, übernimm es in dein Konto. Wenn nicht, lass es: es bleibt hier für den, der es erstellt hat.',
		more: (n: number) => `und ${n} weitere`,
		handedIn: (n: number) =>
			plural(n, {
				one: '# Schüler hat über diese Links abgegeben.',
				other: '# Schüler haben über diese Links abgegeben.',
			}),
		yes: 'Ja, das ist meins: übernehmen',
		no: 'Nein, lassen',
		brought: (n: number) =>
			plural(n, {
				one: 'Deine # Aktivität von vor der Anmeldung ist hier.',
				other: 'Deine # Aktivitäten von vor der Anmeldung sind hier.',
			}),
		broughtOther: 'Was du vor der Anmeldung erstellt hast, ist hier.',
	},
})

/**
 * Asks, once she is signed in, whether the work a guest left in this browser is hers. A school
 * computer keeps a guest for a year, so the work may be a colleague's: nothing moves until she says
 * yes, and no (or closing it) leaves it as it was for whoever made it. Titles and the hand-in count
 * are shown so she can tell. Mounted by the layout every signed-in page sits in, so it is asked on
 * the first page after either way of signing in.
 */
export function GuestWorkPrompt() {
	const t = useT(messages)
	const { data: me } = useMe()
	// A guest has no account to bring work into.
	const signedIn = !!me && !me.guest
	const { data: pending } = usePendingGuest(signedIn)
	const bring = useBringPending()
	const leave = useLeavePending()
	// Closing without an answer asks again on the next page load, until the offer runs out.
	const [later, setLater] = useState(false)
	// Kept through the dialog's exit animation, which outlasts the answer.
	const last = useRef<PendingGuest | null>(null)
	if (pending) last.current = pending
	const shown = pending ?? last.current
	if (!signedIn || !shown) return null

	const answer = () =>
		bring.mutate(shown.guestId, {
			onSuccess: ({ brought }) => {
				if (!brought) return
				toast(brought.activities > 0 ? t.brought(brought.activities) : t.broughtOther, {
					tone: 'ok',
					ms: 8000,
				})
			},
			onError: (err) => toast(errorMessage(err), { tone: 'error' }),
		})

	return (
		<Dialog
			id="guest-work-dialog"
			open={!!pending && !later}
			onClose={() => setLater(true)}
			placement="sheet"
			title={t.title}
			description={t.body}
			footer={
				<>
					<Button
						id="guest-work-leave"
						variant="secondary"
						className="h-auto min-h-11 w-full whitespace-normal py-2 text-center sm:w-auto"
						disabled={bring.isPending}
						loading={leave.isPending}
						onClick={() => leave.mutate()}
					>
						{t.no}
					</Button>
					<Button
						id="guest-work-bring"
						variant="primary"
						className="h-auto min-h-11 w-full whitespace-normal py-2 text-center sm:w-auto"
						disabled={leave.isPending}
						loading={bring.isPending}
						onClick={answer}
					>
						{t.yes}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-3">
				<ul
					id="guest-work-items"
					className="flex flex-col gap-1 rounded-btn bg-canvas p-3 text-body"
				>
					{shown.items.map((item, i) => (
						<li key={`${i}-${item.title}`} className="truncate font-[560] text-ink">
							{item.title}
						</li>
					))}
					{shown.total > shown.items.length && (
						<li className="text-small text-ink-2">{t.more(shown.total - shown.items.length)}</li>
					)}
				</ul>
				{shown.handedIn > 0 && (
					<p id="guest-work-handed-in" className="text-body text-ink-2">
						{t.handedIn(shown.handedIn)}
					</p>
				)}
			</div>
		</Dialog>
	)
}
